import * as React from "react";
import ChevronDown from "lucide-react/icons/chevron-down";
import ChevronUp from "lucide-react/icons/chevron-up";
import ShoppingCart from "lucide-react/icons/shopping-cart";
import { Modal } from "../common";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Lazy load Stripe component only when checkout is initiated
const StripeEmbeddedCheckout = React.lazy(
  () => import("./StripeEmbeddedCheckout")
);
import tierDefinitionsData from "../../data/tierDefinitions.json";

interface TierDefinition {
  id: string;
  name: string;
  price: number;
  priceDisplay: string;
  description: string;
  sku: string;
  popular?: boolean;
  subtitle?: string;
  buttonText: string;
  buttonStyle: "primary" | "secondary";
  features: string[];
  displayFeatures: string[];
  inherits?: string[];
}

interface PricingTiersProps {
  onPurchase?: (tier: string) => void;
  checkoutMode?: "embedded" | "hosted";
  userEmail?: string; // Add user email prop
  compact?: boolean; // Add compact layout option
  /**
   * Which tier gets the hero treatment in compact mode. Defaults to
   * "essential" (the book-first hierarchy the home page wants). The course
   * page passes "professional", because Essential does NOT include the course
   * and must not be the dominant CTA there.
   */
  emphasis?: "essential" | "professional" | "enterprise";
}

// Feature key mappings for each tier - maps display feature index to i18n key
const tierFeatureKeys: Record<string, string[]> = {
  essential: ["completeBook", "pdfEpub", "lifetimeUpdates", "githubAccess"],
  professional: [
    "allEssential",
    "courseSlides",
    "premiumSamples",
    "earlyAccess",
    "prioritySupport",
  ],
  enterprise: ["allProfessional", "teamLicense", "consultingCall", "privateRepo"],
};

const PricingTiers: React.FC<PricingTiersProps> = ({
  onPurchase,
  checkoutMode = "embedded", // Default to embedded checkout
  userEmail = "", // Default to empty string for non-logged-in users
  compact = false, // Default to full layout
  emphasis = "essential",
}) => {
  const { t } = useTranslation("home");
  const [tiers, setTiers] = React.useState<TierDefinition[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedTier, setSelectedTier] = React.useState<string | null>(null);
  const [showCheckout, setShowCheckout] = React.useState(false);
  const [expandedTiers, setExpandedTiers] = React.useState<Set<string>>(
    new Set()
  );

  // Helper to get translated tier data
  const getTranslatedTier = (tier: TierDefinition) => {
    const tierId = tier.id;
    const featureKeys = tierFeatureKeys[tierId] || [];

    return {
      ...tier,
      name: t(`pricing.tiers.${tierId}.name`, tier.name),
      description: t(`pricing.tiers.${tierId}.description`, tier.description),
      buttonText: t(`pricing.tiers.${tierId}.buttonText`, tier.buttonText),
      displayFeatures: featureKeys.map((key, index) =>
        t(`pricing.tiers.${tierId}.features.${key}`, tier.displayFeatures[index] || key)
      ),
    };
  };

  React.useEffect(() => {
    try {
      setLoading(true);
      // Convert imported JSON object to array format
      const tierDefinitions = Object.values(
        tierDefinitionsData
      ) as TierDefinition[];
      setTiers(tierDefinitions);
      setError(null);
    } catch (err) {
      setError("Failed to load pricing information");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleTierExpansion = (tierId: string) => {
    const newExpanded = new Set(expandedTiers);
    if (newExpanded.has(tierId)) {
      newExpanded.delete(tierId);
    } else {
      newExpanded.add(tierId);
    }
    setExpandedTiers(newExpanded);
  };

  const handlePurchaseClick = (tierSku: string) => {
    if (checkoutMode === "embedded") {
      // Use embedded checkout modal
      setSelectedTier(tierSku);
      setShowCheckout(true);
    } else {
      // Use hosted checkout via callback
      if (onPurchase) {
        onPurchase(tierSku);
      }
    }
  };

  const handleCheckoutSuccess = async (
    paymentIntentId: string,
    customerEmail: string
  ) => {
    setShowCheckout(false);
    setSelectedTier(null);

    // Navigate to dashboard with payment info
    const searchParams = new URLSearchParams({
      payment_intent: paymentIntentId,
      email: customerEmail,
      success: "true",
    });

    window.location.href = `/dashboard?${searchParams.toString()}`;
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setSelectedTier(null);
  };

  const handleCheckoutError = (error: string) => {
    // Keep modal open and show error in EmbeddedCheckout component
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto animate-pulse">
        {/* Loading tier selector */}
        <div className="bg-secondary/30 rounded-2xl p-2 mb-12">
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 bg-tertiary rounded-md p-6">
                <div className="h-4 bg-secondary rounded mb-2 mx-auto w-16"></div>
                <div className="h-8 bg-secondary rounded mx-auto w-20"></div>
                <div className="h-3 bg-tertiary rounded mt-2 mx-auto w-24"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Loading content */}
        <div className="space-y-8">
          <div className="text-center">
            <div className="h-10 bg-secondary rounded w-48 mx-auto mb-4"></div>
            <div className="h-5 bg-tertiary rounded w-96 mx-auto"></div>
          </div>

          <div className="bg-primary/50 rounded-2xl p-8">
            <div className="h-6 bg-secondary rounded w-40 mx-auto mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((j) => (
                <div
                  key={j}
                  className="flex items-center p-4 bg-primary/60 rounded-md"
                >
                  <div className="h-8 w-8 bg-accent/20 rounded-full mr-4"></div>
                  <div className="h-4 bg-tertiary rounded flex-1"></div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <div className="h-14 bg-accent rounded-2xl w-64 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-primary/50 backdrop-blur-sm border border-secondary/50 rounded-2xl p-8 text-center">
          <svg
            className="w-12 h-12 text-tertiary mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-primary mb-2">
            {t("pricing.failedToLoad")}
          </h3>
          <p className="text-secondary mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-accent text-invert px-8 py-3 rounded-md font-semibold hover:brightness-110 transition-all"
          >
            {t("pricing.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  const essentialTierRaw = tiers.find((tier) => tier.id === "essential");
  const essentialTier = essentialTierRaw ? getTranslatedTier(essentialTierRaw) : null;
  const otherTiers = tiers.filter((tier) => tier.id !== "essential").map(getTranslatedTier);

  // Compact mode promotes one tier to the hero slot. The Amazon link and the
  // "instant download" line describe the book purchase specifically, so they
  // only render when the hero actually is Essential.
  const heroRaw = tiers.find((tier) => tier.id === emphasis) ?? essentialTierRaw;
  const heroTier = heroRaw ? getTranslatedTier(heroRaw) : null;
  const heroIsEssential = heroTier?.id === "essential";
  const restTiers = tiers
    .filter((tier) => tier.id !== heroTier?.id)
    .map(getTranslatedTier);

  // Compact Layout
  if (compact) {
    return (
      <>
        <div className="space-y-4">
          {/* Essential Tier - Keep Original Style */}
          {heroTier && (
            <div className="bg-gradient-to-br from-accent/5 to-accent/10 border border-accent/20 rounded p-4 text-center">
              <div className="flex items-baseline justify-center mb-3">
                <span className="text-4xl font-bold text-primary">
                  {heroTier.priceDisplay}
                </span>
                <span className="text-base text-secondary ml-2">
                  {heroIsEssential ? t("pricing.digitalEdition") : heroTier.name}
                </span>
              </div>

              {/* Clear digital indicator - book purchase only */}
              {heroIsEssential && (
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg
                  className="w-4 h-4 text-accent"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm text-accent font-medium">
                  {t("pricing.instantDownload")}
                </span>
              </div>
              )}

              {/* Essential Features - Streamlined */}
              <ul className="space-y-1 mb-4 text-left">
                {heroTier.displayFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <svg
                      className="w-4 h-4 text-accent mr-2 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-primary">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Primary CTA */}
              <button
                onClick={() => handlePurchaseClick(heroTier.sku)}
                className="bg-accent text-white text-invert py-2 w-full px-6 rounded-md font-semibold hover:brightness-110 transition-all"
              >
                {heroIsEssential
                  ? t("pricing.getDigitalEdition")
                  : heroTier.buttonText}
              </button>

              {/* Print Edition on Amazon - book purchase only */}
              {heroIsEssential && (
              <a
                href="https://www.amazon.com/dp/B0G2BCQQJY"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full mt-2 py-2 px-6 border border-secondary/50 text-secondary hover:border-accent/50 hover:text-accent rounded-md text-center flex items-center justify-center gap-2 transition-colors"
              >
                <ShoppingCart className="w-4 h-4" />
                {t("pricing.buyOnAmazon")}
              </a>
              )}
            </div>
          )}

          {/* Professional and Enterprise - Collapsible */}
          {restTiers.map((tier) => {
            const isExpanded = expandedTiers.has(tier.id);

            return (
              <div
                key={tier.id}
                className="bg-primary/50 border border-secondary/30 hover:border-accent/30 rounded-lg transition-all duration-200"
              >
                {/* Collapsed Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-primary">
                          {tier.name}
                        </h3>
                        {tier.popular && (
                          <span className="bg-accent/10 text-accent px-2 py-0.5 rounded-full text-xs font-medium">
                            {t("pricing.popular")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary">
                        {tier.description}
                      </p>
                      <button
                        onClick={() => toggleTierExpansion(tier.id)}
                        className="text-xs text-accent hover:text-accent/80 transition-colors mt-1 flex items-center gap-1"
                      >
                        {t("pricing.whatsIncluded")}
                        {isExpanded ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        )}
                      </button>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary">
                          {tier.priceDisplay}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Button on separate line */}
                  <button
                    onClick={() => handlePurchaseClick(tier.sku)}
                    className="w-full bg-secondary text-primary hover:bg-accent hover:text-white border border-secondary px-4 py-2 rounded-md font-medium text-sm transition-all"
                  >
                    {tier.buttonText}
                  </button>
                </div>

                {/* Expanded Features */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-secondary/20">
                    <div className="pt-3 space-y-2">
                      {tier.displayFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start text-sm">
                          <svg
                            className="w-4 h-4 text-accent mr-2 flex-shrink-0 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-secondary">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* University License Link */}
        <div className="text-center mt-4">
          <Link
            to="/university"
            className="text-xs text-secondary hover:text-accent transition-colors"
          >
            {t("university.linkText")}
          </Link>
        </div>

        {/* Embedded Checkout Modal */}
        {showCheckout && selectedTier && (
          <Modal
            isOpen={showCheckout}
            onClose={handleCheckoutCancel}
            title={t("pricing.secureCheckout")}
            size="xl"
          >
            <React.Suspense
              fallback={
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                  <span className="ml-3 text-secondary">
                    {t("pricing.loadingCheckout")}
                  </span>
                </div>
              }
            >
              <StripeEmbeddedCheckout
                sku={selectedTier}
                userEmail={userEmail}
                onSuccess={handleCheckoutSuccess}
                onCancel={handleCheckoutCancel}
                onError={handleCheckoutError}
              />
            </React.Suspense>
          </Modal>
        )}
      </>
    );
  }

  // Original Full Layout
  return (
    <>
      <div className="max-w-lg mx-auto  ">
        {/* Essential Tier Hero */}
        {essentialTier && (
          <div className="bg-gradient-to-br from-accent/5 to-accent/10 border border-accent/20 rounded p-4 mb-6 text-center">
            {/* <h2 className="text-2xl font-bold mb-2 text-primary">
              Get the Book
            </h2> */}

            <div className="flex items-baseline justify-center mb-3">
              <span className="text-4xl font-bold text-primary">
                {essentialTier.priceDisplay}
              </span>
              <span className="text-base text-secondary ml-2">
                {t("pricing.digitalEdition")}
              </span>
            </div>

            {/* Clear digital indicator */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <svg
                className="w-4 h-4 text-accent"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <span className="text-sm text-accent font-medium">
                {t("pricing.instantDownload")}
              </span>
            </div>

            {/* Essential Features - Streamlined */}
            <ul className="space-y-1 mb-4 text-left">
              {essentialTier.displayFeatures.map((feature, index) => (
                <li key={index} className="flex items-start text-sm">
                  <svg
                    className="w-4 h-4 text-accent mr-2 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-primary">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Primary CTA */}
            <button
              onClick={() => handlePurchaseClick(essentialTier.sku)}
              className="bg-accent text-white text-invert py-2 w-full px-6 rounded-md font-semibold hover:brightness-110 transition-all"
            >
              {t("pricing.getDigitalEdition")}
            </button>

            {/* Print Edition on Amazon */}
            <a
              href="https://www.amazon.com/dp/B0G2BCQQJY"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full mt-2 py-2 px-6 border border-secondary/50 text-secondary hover:border-accent/50 hover:text-accent rounded-md text-center flex items-center justify-center gap-2 transition-colors"
            >
              <ShoppingCart className="w-4 h-4" />
              {t("pricing.buyOnAmazon")}
            </a>
          </div>
        )}

        {/* Secondary Options - Other Tiers */}
        {otherTiers.length > 0 && (
          <div className="border-t border-secondary/30 pt-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-primary mb-1">
                {t("pricing.needMore")}
              </h3>
              <p className="text-sm text-secondary">
                {t("pricing.additionalPackages")}
              </p>
            </div>

            <div className="grid gap-4">
              {otherTiers.map((tier) => (
                <div
                  key={tier.id}
                  className="bg-primary/30 border border-secondary/50 rounded-md p-4 hover:border-accent/30 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-primary">
                          {tier.name}
                        </h4>
                        {tier.popular && (
                          <span className="bg-accent/10 text-accent px-2 py-0.5 rounded-full text-xs font-bold">
                            {t("pricing.popular")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-secondary mt-1">
                        {tier.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        {tier.priceDisplay}
                      </div>
                      <button
                        onClick={() => handlePurchaseClick(tier.sku)}
                        className="mt-2 bg-secondary text-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent hover:text-invert transition-all"
                      >
                        {tier.buttonText}
                      </button>
                    </div>
                  </div>

                  {/* Full Feature List */}
                  <ul className="space-y-2">
                    {tier.displayFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <svg
                          className="w-4 h-4 text-accent mr-2 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* University License Link */}
        <div className="text-center mt-6">
          <Link
            to="/university"
            className="text-sm text-secondary hover:text-accent transition-colors"
          >
            {t("university.linkText")}
          </Link>
        </div>
      </div>

      {/* Embedded Checkout Modal */}
      {showCheckout && selectedTier && (
        <Modal
          isOpen={showCheckout}
          onClose={handleCheckoutCancel}
          title={t("pricing.secureCheckout")}
          size="xl"
        >
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <span className="ml-3 text-secondary">{t("pricing.loadingCheckout")}</span>
              </div>
            }
          >
            <StripeEmbeddedCheckout
              sku={selectedTier}
              userEmail={userEmail}
              onSuccess={handleCheckoutSuccess}
              onCancel={handleCheckoutCancel}
              onError={handleCheckoutError}
            />
          </React.Suspense>
        </Modal>
      )}
    </>
  );
};

export default PricingTiers;
